import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackScrollDepth, 
  trackEngagementTime, 
  trackHighIntentUser,
  pushToDataLayer 
} from '@/lib/gtm';

interface EngagementData {
  scrollDepths: Set<number>;
  maxScrollDepth: number;
  timeOnPage: number;
  pageVisits: number;
}

// Scroll depth milestones to track (percentages)
const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

// Time milestones to track (seconds)
const TIME_MILESTONES = [30, 60, 120, 180, 300];

export const useEngagementTracking = () => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const trackedScrollDepthsRef = useRef<Set<number>>(new Set());
  const trackedTimeMilestonesRef = useRef<Set<number>>(new Set());
  const engagementDataRef = useRef<EngagementData>({
    scrollDepths: new Set(),
    maxScrollDepth: 0,
    timeOnPage: 0,
    pageVisits: 1,
  });

  // Calculate scroll depth percentage
  const calculateScrollDepth = useCallback((): number => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    
    if (documentHeight <= windowHeight) return 100;
    
    const scrollPercentage = Math.round(
      (scrollTop / (documentHeight - windowHeight)) * 100
    );
    
    return Math.min(100, Math.max(0, scrollPercentage));
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const currentDepth = calculateScrollDepth();
    
    // Update max scroll depth
    if (currentDepth > engagementDataRef.current.maxScrollDepth) {
      engagementDataRef.current.maxScrollDepth = currentDepth;
    }
    
    // Track milestone depths
    SCROLL_MILESTONES.forEach((milestone) => {
      if (
        currentDepth >= milestone && 
        !trackedScrollDepthsRef.current.has(milestone)
      ) {
        trackedScrollDepthsRef.current.add(milestone);
        engagementDataRef.current.scrollDepths.add(milestone);
        trackScrollDepth(milestone);
        
        // Push detailed event to dataLayer
        pushToDataLayer({
          event: 'scroll_milestone',
          scroll_depth: milestone,
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    });
  }, [calculateScrollDepth, location.pathname]);

  // Track time on page
  const trackTimeOnPage = useCallback(() => {
    const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    engagementDataRef.current.timeOnPage = elapsedSeconds;
    
    // Track time milestones
    TIME_MILESTONES.forEach((milestone) => {
      if (
        elapsedSeconds >= milestone && 
        !trackedTimeMilestonesRef.current.has(milestone)
      ) {
        trackedTimeMilestonesRef.current.add(milestone);
        trackEngagementTime(milestone);
        
        // Push detailed event to dataLayer
        pushToDataLayer({
          event: 'time_milestone',
          time_on_page: milestone,
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    });
    
    // Check for high intent user (3+ minutes on site)
    if (elapsedSeconds >= 180) {
      const pageVisits = parseInt(sessionStorage.getItem('page_visit_count') || '1', 10);
      trackHighIntentUser(pageVisits, elapsedSeconds);
    }
  }, [location.pathname]);

  // Track page visibility changes
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // User left the page - track final engagement
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      
      pushToDataLayer({
        event: 'page_engagement_end',
        time_on_page: elapsedSeconds,
        max_scroll_depth: engagementDataRef.current.maxScrollDepth,
        page_path: location.pathname,
      });
    } else {
      // User returned - reset timer
      startTimeRef.current = Date.now();
    }
  }, [location.pathname]);

  // Track before unload
  const handleBeforeUnload = useCallback(() => {
    const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    // Use sendBeacon for reliable tracking on page exit
    if (navigator.sendBeacon) {
      const data = JSON.stringify({
        event: 'page_exit',
        time_on_page: elapsedSeconds,
        max_scroll_depth: engagementDataRef.current.maxScrollDepth,
        scroll_milestones_reached: Array.from(engagementDataRef.current.scrollDepths),
        page_path: location.pathname,
      });
      
      // This would typically send to an analytics endpoint
      // For now, we just push to dataLayer
    }
    
    pushToDataLayer({
      event: 'page_exit',
      time_on_page: elapsedSeconds,
      max_scroll_depth: engagementDataRef.current.maxScrollDepth,
      page_path: location.pathname,
    });
  }, [location.pathname]);

  // Initialize tracking
  useEffect(() => {
    // Reset tracking state for new page
    startTimeRef.current = Date.now();
    trackedScrollDepthsRef.current.clear();
    trackedTimeMilestonesRef.current.clear();
    engagementDataRef.current = {
      scrollDepths: new Set(),
      maxScrollDepth: 0,
      timeOnPage: 0,
      pageVisits: engagementDataRef.current.pageVisits + 1,
    };

    // Track page visit count in session
    const currentCount = parseInt(sessionStorage.getItem('page_visit_count') || '0', 10);
    sessionStorage.setItem('page_visit_count', (currentCount + 1).toString());

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Set up time tracking interval
    const timeInterval = setInterval(trackTimeOnPage, 5000);

    // Initial scroll check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(timeInterval);
    };
  }, [location.pathname, handleScroll, handleVisibilityChange, handleBeforeUnload, trackTimeOnPage]);

  return {
    getEngagementData: () => ({
      maxScrollDepth: engagementDataRef.current.maxScrollDepth,
      timeOnPage: engagementDataRef.current.timeOnPage,
      scrollMilestones: Array.from(engagementDataRef.current.scrollDepths),
    }),
  };
};
